import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import api from '../utils/api';
import { getSession } from '../utils/session';
import cs from './locales/cs-CZ.json';
import en from './locales/en.json';

const LANGUAGE_COOKIE = 'language';
const DEFAULT_LANGUAGE = 'en';
const TRANSLATIONS = {
  en,
  'cs-CZ': cs,
};

const createEmptyTenantTranslations = () => ({
  en: {},
  'cs-CZ': {},
});

export const AVAILABLE_LANGUAGES = [
  { value: 'en', labelKey: 'shared.languages.english' },
  { value: 'cs-CZ', labelKey: 'shared.languages.czech' },
];

function resolveLanguage(candidate) {
  if (TRANSLATIONS[candidate]) {
    return candidate;
  }

  const normalized = `${candidate || ''}`.toLowerCase();

  if (normalized.startsWith('cs')) {
    return 'cs-CZ';
  }

  return DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
  const savedLanguage = Cookies.get(LANGUAGE_COOKIE);

  if (savedLanguage) {
    return resolveLanguage(savedLanguage);
  }

  if (typeof window !== 'undefined') {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_COOKIE);

    if (storedLanguage) {
      return resolveLanguage(storedLanguage);
    }

    return resolveLanguage(window.navigator.language);
  }

  return DEFAULT_LANGUAGE;
}

function interpolate(value, params) {
  return value.replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (_, moustacheKey, simpleKey) => {
    const key = moustacheKey || simpleKey;

    if (params[key] === undefined || params[key] === null) {
      return '';
    }

    return String(params[key]);
  });
}

function normalizeTenantTranslations(bundle) {
  return AVAILABLE_LANGUAGES.reduce((acc, { value }) => {
    acc[value] =
      bundle?.[value] && typeof bundle[value] === 'object'
        ? bundle[value]
        : {};
    return acc;
  }, {});
}

function translateMessage(language, key, options = {}, tenantTranslations = {}) {
  const messages = {
    ...(TRANSLATIONS[language] || TRANSLATIONS[DEFAULT_LANGUAGE]),
    ...(tenantTranslations[language] || {}),
  };
  const fallbackMessages = {
    ...TRANSLATIONS[DEFAULT_LANGUAGE],
    ...(tenantTranslations[DEFAULT_LANGUAGE] || {}),
  };
  const rawValue = messages[key] ?? fallbackMessages[key];

  if (typeof rawValue === 'string') {
    return interpolate(rawValue, options);
  }

  return options.defaultValue ?? key;
}

const I18nContext = createContext({
  availableLanguages: AVAILABLE_LANGUAGES,
  changeLanguage: () => {},
  getLanguage: () => DEFAULT_LANGUAGE,
  language: DEFAULT_LANGUAGE,
  t: (key, options) => options?.defaultValue ?? key,
});

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [tenantTranslations, setTenantTranslations] = useState(
    createEmptyTenantTranslations,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    Cookies.set(LANGUAGE_COOKIE, language, { expires: 365 });
    window.localStorage.setItem(LANGUAGE_COOKIE, language);
    document.documentElement.lang = language;
  }, [language]);

  const refreshTenantTranslations = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const { tenantId } = getSession();
    if (!tenantId) {
      setTenantTranslations(createEmptyTenantTranslations());
      return;
    }

    try {
      const res = await api.get(`/api/v1/tenants/${tenantId}/translations/bundle`);
      setTenantTranslations(
        normalizeTenantTranslations(res.data?.data?.translations),
      );
    } catch {
      setTenantTranslations(createEmptyTenantTranslations());
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    refreshTenantTranslations();
    window.addEventListener('eclipse:tenant-changed', refreshTenantTranslations);
    window.addEventListener(
      'eclipse:translations-changed',
      refreshTenantTranslations,
    );

    return () => {
      window.removeEventListener(
        'eclipse:tenant-changed',
        refreshTenantTranslations,
      );
      window.removeEventListener(
        'eclipse:translations-changed',
        refreshTenantTranslations,
      );
    };
  }, [refreshTenantTranslations]);

  const changeLanguage = (nextLanguage) => {
    setLanguage(resolveLanguage(nextLanguage));
  };

  const getLanguage = () => language;

  const t = (key, options) =>
    translateMessage(language, key, options, tenantTranslations);

  return (
    <I18nContext.Provider
      value={{
        availableLanguages: AVAILABLE_LANGUAGES,
        changeLanguage,
        getLanguage,
        language,
        t,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useTranslate() {
  const { t } = useI18n();

  return { t };
}
